/**
 * OperationalStateStore — the ONE truth for upload/basket/review state.
 *
 * Constitutional reference: /app/memory/PLATFORM_REBUILD_DOCTRINE.md §C.
 *   "There must be ONE operational authority. All pages SUBSCRIBE to it."
 *
 * Architecture reference: /app/memory/PLATFORM_REBUILD_ARCHITECTURE.md §4.
 *
 * What this is:
 *   A Context + useReducer pair that tracks every operational entity on
 *   the golden path:
 *     - baskets   keyed by basket_id
 *     - uploads   keyed by upload_id
 *     - reviews   keyed by review_id
 *   Plus a single trace_id per session.
 *
 * What this is NOT (Phase A scope discipline):
 *   - This does NOT yet replace ClipBasketV2's internal draft state.
 *     The existing basket continues to own its rows. The store is
 *     SHADOW-DEPLOYED in Phase A: anything new (upload-lab, lifecycle
 *     drawer, future result surfaces) writes through the store; the
 *     existing V2 modal will be wired in Phase B.
 *   - This does NOT persist itself. Persistence is handled by the
 *     basket draft (clipBasketDraft.js) and the backend. The store
 *     is a runtime projection — boot snapshot rehydrated on mount,
 *     reconciled with the server.
 *
 * Doctrine rules enforced here:
 *   - ONE store. Importing this module twice still produces one Context.
 *   - Authorities write via `dispatch(...)`. Pages subscribe via
 *     `useOperationalState()`. The hook returns READ-ONLY data; there
 *     is no `setState` exposed.
 */

import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

const OperationalStateContext = createContext(null);

const initialState = {
  // Per-session identifiers — created on mount, persist for the
  // lifetime of the page (NOT across reloads — those rehydrate).
  trace_id: null,

  // Keyed maps. Lookup by ID is the canonical access pattern.
  baskets: {},   // basket_id -> { basket_id, intent, status, row_count, created_at, updated_at, trace_id }
  uploads: {},   // upload_id -> { upload_id, basket_id?, status, total_bytes, bytes_received, total_chunks, received_chunks, last_event_at }
  reviews: {},   // review_id -> { review_id, basket_id?, intent, status, created_at, updated_at }

  // Lifecycle event tape — last 200 events, in arrival order (newest
  // last for natural append; subscribers reverse for display).
  events: [],

  // Derived flags for upload-protected mode (doctrine §9).
  // Computed on every state change in the reducer.
  upload_active: false,
};

function makeTraceId() {
  const now = new Date();
  const stamp = [
    now.getUTCFullYear(),
    String(now.getUTCMonth() + 1).padStart(2, "0"),
    String(now.getUTCDate()).padStart(2, "0"),
    String(now.getUTCHours()).padStart(2, "0"),
    String(now.getUTCMinutes()).padStart(2, "0"),
    String(now.getUTCSeconds()).padStart(2, "0"),
  ].join("");
  const rand = Math.floor(Math.random() * 0x10000).toString(16).padStart(4, "0");
  return `tr_${stamp}_${rand}`;
}

function recomputeUploadActive(uploads) {
  for (const u of Object.values(uploads)) {
    if (u && u.status && ["initiating", "uploading", "completing", "registering"].includes(u.status)) {
      return true;
    }
  }
  return false;
}

function reducer(state, action) {
  switch (action.type) {
    case "TRACE_ID_SET":
      return state.trace_id ? state : { ...state, trace_id: action.trace_id };

    case "BASKET_UPSERT": {
      const b = action.basket;
      const baskets = { ...state.baskets, [b.basket_id]: { ...state.baskets[b.basket_id], ...b, updated_at: action.at } };
      return { ...state, baskets };
    }

    case "UPLOAD_UPSERT": {
      const u = action.upload;
      const uploads = { ...state.uploads, [u.upload_id]: { ...state.uploads[u.upload_id], ...u, last_event_at: action.at } };
      return { ...state, uploads, upload_active: recomputeUploadActive(uploads) };
    }

    case "REVIEW_UPSERT": {
      const r = action.review;
      const reviews = { ...state.reviews, [r.review_id]: { ...state.reviews[r.review_id], ...r, updated_at: action.at } };
      return { ...state, reviews };
    }

    case "EVENT_APPEND": {
      // Cap at last 200 events. Newest at the end (reverse for display).
      const events = state.events.length >= 200
        ? [...state.events.slice(-199), action.event]
        : [...state.events, action.event];
      return { ...state, events };
    }

    case "HYDRATE":
      // Boot rehydration. Caller passes a partial state snapshot
      // (e.g. from clipBasketDraft on mount). Never overwrites a
      // freshly-set trace_id.
      return {
        ...state,
        baskets: { ...state.baskets, ...(action.snapshot.baskets || {}) },
        uploads: { ...state.uploads, ...(action.snapshot.uploads || {}) },
        reviews: { ...state.reviews, ...(action.snapshot.reviews || {}) },
        upload_active: recomputeUploadActive({ ...state.uploads, ...(action.snapshot.uploads || {}) }),
      };

    default:
      return state;
  }
}

export function OperationalStateProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const traceIdRef = useRef(null);

  // Lazily create a trace_id on first mount. Stable for the page session.
  useEffect(() => {
    if (!traceIdRef.current) {
      traceIdRef.current = makeTraceId();
      dispatch({ type: "TRACE_ID_SET", trace_id: traceIdRef.current });
    }
  }, []);

  // Public dispatcher API. Each function is the ONLY sanctioned way
  // to mutate a category of state. New mutation paths must be added
  // here, never inside subscribers.
  const upsertBasket = useCallback((basket) => {
    dispatch({ type: "BASKET_UPSERT", basket, at: new Date().toISOString() });
  }, []);

  const upsertUpload = useCallback((upload) => {
    dispatch({ type: "UPLOAD_UPSERT", upload, at: new Date().toISOString() });
  }, []);

  const upsertReview = useCallback((review) => {
    dispatch({ type: "REVIEW_UPSERT", review, at: new Date().toISOString() });
  }, []);

  const appendEvent = useCallback((event_name, payload = {}) => {
    dispatch({
      type: "EVENT_APPEND",
      event: {
        event_id: `ev_${Date.now()}_${Math.floor(Math.random() * 0x10000).toString(16)}`,
        server_time: new Date().toISOString(),
        trace_id: traceIdRef.current,
        event_name,
        payload,
      },
    });
  }, []);

  const hydrate = useCallback((snapshot) => {
    dispatch({ type: "HYDRATE", snapshot });
  }, []);

  const value = useMemo(() => ({
    state,
    upsertBasket,
    upsertUpload,
    upsertReview,
    appendEvent,
    hydrate,
  }), [state, upsertBasket, upsertUpload, upsertReview, appendEvent, hydrate]);

  return (
    <OperationalStateContext.Provider value={value}>
      {children}
    </OperationalStateContext.Provider>
  );
}

/**
 * Subscribe hook. Returns READ-ONLY state plus the appendEvent
 * function (event logging is read-side observability, not state
 * mutation). For state mutation, authorities use the dedicated
 * dispatchers via `useOperationalDispatch()` below.
 */
export function useOperationalState() {
  const ctx = useContext(OperationalStateContext);
  if (!ctx) {
    // Throwing here is deliberate: a page reading operational state
    // outside the provider is a doctrine violation that we want to
    // surface loudly, not silently fall back.
    throw new Error("useOperationalState() must be called inside <OperationalStateProvider>.");
  }
  return { state: ctx.state, appendEvent: ctx.appendEvent };
}

/**
 * Authority hook. Returns the mutation dispatchers. Callers of this
 * hook are AUTHORITIES (basket, upload, recovery). Pages MUST use
 * `useOperationalState()` instead.
 *
 * The split is enforced by doctrine guard, not by JavaScript — the
 * hook is exported and React doesn't know "who" is calling. The
 * doctrine guard scans for which files import this hook and rejects
 * non-authority files.
 */
export function useOperationalDispatch() {
  const ctx = useContext(OperationalStateContext);
  if (!ctx) {
    throw new Error("useOperationalDispatch() must be called inside <OperationalStateProvider>.");
  }
  return {
    upsertBasket: ctx.upsertBasket,
    upsertUpload: ctx.upsertUpload,
    upsertReview: ctx.upsertReview,
    hydrate: ctx.hydrate,
  };
}

/**
 * Optional observability hook. Returns `{ appendEvent }`. When the
 * caller is INSIDE the provider, appendEvent writes to the operational
 * store's event tape (and the lifecycle drawer's copy/export bundle).
 * When the caller is OUTSIDE the provider, appendEvent is a no-op.
 *
 * This exists so legacy upload-touching components (e.g. ClipBasketV2,
 * which today mounts both inside `/upload-lab` and from other surfaces
 * that don't wrap a provider) can be instrumented WITHOUT crashing on
 * the non-provider mounts. It is OBSERVABILITY-ONLY — it returns no
 * mutators, so no doctrine §C boundary is crossed.
 */
export function useOperationalLog() {
  const ctx = useContext(OperationalStateContext);
  if (!ctx) {
    return { appendEvent: () => {} };
  }
  return { appendEvent: ctx.appendEvent };
}

export default OperationalStateProvider;
